import {useApolloClient, useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    GetUsersWithQuotas as GetUsersWithQuotasQuery,
    GetUsersWithQuotasVariables,
    QuotaDelete as QuotaDeleteMutation,
    QuotaDeleteVariables,
    QuotaDeleteInput,
} from 'nutshell-graphql-types';

import DELETE_QUOTA from '../mutations/delete-quota.graphql';
import GET_USERS_WITH_QUOTAS from '../queries/get-users-with-quotas.graphql';

import {removeQuotaFromYearOverviewStatsCache} from './helpers';
import {QUERY_VARIABLES} from './use-get-users-with-quotas';

export function useDeleteQuota(): {
    deleteQuota: (input: QuotaDeleteInput) => Promise<any>;
    isLoading: boolean;
    errorMessage: string | null | undefined;
} {
    const client = useApolloClient();
    const [mutate, {loading, error}] = useMutation<QuotaDeleteMutation, QuotaDeleteVariables>(
        DELETE_QUOTA
    );

    return {
        deleteQuota: (input: QuotaDeleteInput) => {
            const previousUsersWithQuotas = client.readQuery<
                GetUsersWithQuotasQuery,
                GetUsersWithQuotasVariables
            >({
                query: GET_USERS_WITH_QUOTAS,
                variables: QUERY_VARIABLES,
            });

            return mutate({
                variables: {input},
                update: (cache) => {
                    const existingUsers = cache.readQuery<
                        GetUsersWithQuotasQuery,
                        GetUsersWithQuotasVariables
                    >({
                        query: GET_USERS_WITH_QUOTAS,
                        variables: QUERY_VARIABLES,
                    });

                    if (!existingUsers) return;

                    const updatedUsers = existingUsers.usersWithQuotas.edges
                        .map((edge) => {
                            if (edge.node.id !== input.userId) return edge;

                            const remainingQuotas = edge.node.quotas.filter(
                                (q) => q.fiscalYear !== input.fiscalYear
                            );

                            if (remainingQuotas.length === 0) return null;

                            return {
                                ...edge,
                                node: {
                                    ...edge.node,
                                    quotas: remainingQuotas,
                                },
                            };
                        })
                        .filter((edge): edge is NonNullable<typeof edge> => edge !== null);

                    cache.writeQuery<GetUsersWithQuotasQuery, GetUsersWithQuotasVariables>({
                        query: GET_USERS_WITH_QUOTAS,
                        variables: QUERY_VARIABLES,
                        data: {
                            ...existingUsers,
                            usersWithQuotas: {
                                ...existingUsers.usersWithQuotas,
                                edges: updatedUsers,
                            },
                        },
                    });

                    removeQuotaFromYearOverviewStatsCache(
                        previousUsersWithQuotas,
                        cache,
                        input.userId,
                        input.fiscalYear
                    );
                },
            });
        },
        isLoading: loading,
        // @ts-expect-error
        errorMessage: safelyGetGraphQLErrorMessage(error),
    };
}
